<script setup lang="ts">

import AppContainer from '@apparatix/library/AppContainer.vue';
import Avatar from 'primevue/avatar';
import Button from 'primevue/button';
import FileUpload, { type FileUploadSelectEvent } from 'primevue/fileupload';
import { nextTick, ref } from 'vue';
import { Section } from '@apparatix/library/core';
import Typography from '@apparatix/library/core/typography/Typography.ts';
import { useEndpoint } from '@apparatix/composables';
import UserRoleGate from '@apparatix/library/UserRoleGate.vue';

const file = ref<File | null>(null);
const message = ref('');
const error = ref('');
const warning = ref('');

const interval = ref('28');
const headerRow = ref(1);
const firstDataRow = ref(2);
const downloadPreUpdateImpressions = ref(true);

const impressionsIntervalTooltip = 'Have the impressions been provided as 4-week impressions, weekly impressions, or daily impressions?';
const headerRowTooltip = 'The row containing the headers: Faces, Impressions.';
const firstDataRowTooltip = 'The row containing the first face/impression data.';
const downloadPreUpdateImpressionsTooltip = 'Downloads face impressions from before the update. To revert an update, upload this file and set Impressions Interval to Daily.';

type PreUpdateImpression = {
  face: string;
  impressions: string | number | null;
};

type UploadMeasurementsResponse = {
  success: boolean;
  message?: string;
  warning?: string;
  validCount?: number;
  invalidCount?: number;
  invalidFaces?: string[];
  skippedRowCount?: number;
  skippedRows?: unknown[];
  preUpdateImpressions?: PreUpdateImpression[];
};

type UploadMeasurementsError = {
  error?: string;
  message?: string;
  errors?: Record<string, string[]>;
};

const formatUploadError = (response?: UploadMeasurementsError) => {
  if (response?.error) {
    return response.error;
  }

  if (response?.errors) {
    return Object.values(response.errors).flat().join('\n');
  }

  if (response?.message) {
    return response.message;
  }

  return 'Upload failed.';
};

const { getResults: uploadMeasurements, isLoading: isUploading } = useEndpoint<UploadMeasurementsResponse, UploadMeasurementsError>({
  type: 'POST',
  url: '/manage-measurements/upload',
  successField: 'success',
  onError: response => {
    error.value = formatUploadError(response);
  },
  toast: { showOnSuccess: false, showOnError: false },
});

const handleFileSelect = (event: FileUploadSelectEvent) => {
  file.value = (event.files as File[])[0] ?? null;
  message.value = '';
  error.value = '';
  warning.value = '';
};

const escapeCsvValue = (value: string | number | null | undefined) => {
  const stringValue = value === null || value === undefined ? '' : String(value);

  if (!(/[",\n\r]/).test(stringValue)) {
    return stringValue;
  }

  return `"${stringValue.replace(/"/g, '""')}"`;
};

const downloadPreUpdateImpressionsCsv = (rows: PreUpdateImpression[]) => {
  if (rows.length === 0) {
    return;
  }

  const csv = [
    ['Faces', 'Impressions'],
    ...rows.map(row => [row.face, row.impressions]),
  ]
    .map(row => row.map(escapeCsvValue).join(','))
    .join('\n');

  const blob = new Blob([`${csv}\n`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `pre-update-impressions-${filenameTimestamp()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const filenameTimestamp = () => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');

  return [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
  ].join('-') + `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const uploadFile = async () => {
  message.value = '';
  error.value = '';
  warning.value = '';
  if (!file.value || isUploading.value) {
    return;
  }

  const formData = new FormData();
  formData.append('file', file.value);
  formData.append('interval', interval.value);
  formData.append('headerRow', String(headerRow.value));
  formData.append('firstDataRow', String(firstDataRow.value));
  formData.append('downloadPreUpdateImpressions', downloadPreUpdateImpressions.value ? '1' : '0');

  const response = await uploadMeasurements<UploadMeasurementsResponse, FormData>(formData);

  if (!response) {
    return;
  }

  message.value = response.message ?? '';
  warning.value = response.warning ?? '';

  if (downloadPreUpdateImpressions.value) {
    await nextTick();
    downloadPreUpdateImpressionsCsv(response.preUpdateImpressions ?? []);
  }

  file.value = null;
};

</script>

<template>
  <AppContainer>
    <div class="mb-4">
      <div class="flex items-center gap-2">
        <Avatar
          icon="pi pi-gauge"
          size="normal"
          shape="circle"
          style="background-color: var(--p-blue-100); color: var(--p-blue-500);"
        />
        <h1 class="text-lg">
          Manage Measurements
        </h1>
      </div>
    </div>
    <UserRoleGate :allow-for="['Technologist']">
      <Section :footer="false">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <Typography class="pb-2" variant="section-heading">
              Upload Impressions
            </Typography>
            <Typography class="pb-2" variant="section-subheading">
              Upload PerView impressions via a csv, xls, or xlsx file with two columns: Face, Impressions.<br>
              Face names must match the names as seen in CORE > Inventory > Faces in the REF column. Please remove any prefixes before attempting to upload.<br><br>
              Impressions will be loaded into the relevant face's DEC column.
            </Typography>

            <form @submit.prevent="uploadFile">
              <div class="mt-4">
                <label for="file" class="mb-1 block font-bold">Select File</label>
                <FileUpload
                  mode="basic"
                  accept=".csv,.xls,.xlsx"
                  choose-label="Choose File"
                  custom-upload
                  :choose-button-props="{ size: 'small' }"
                  @select="handleFileSelect"
                />
              </div>

              <div class="mt-4">
                <div class="mb-1 flex items-center gap-1">
                  <label for="interval" class="mb-1 block font-bold">Impressions Interval</label>
                  <Button
                    v-tooltip="impressionsIntervalTooltip"
                    icon="pi pi-question-circle"
                    variant="text"
                    rounded
                    severity="secondary"
                    size="small"
                    type="button"
                    aria-label="Impressions interval help"
                    class="!h-6 !w-6 !p-0"
                  />
                </div>
                <div id="interval">
                  <label class="mb-1 block font-bold"><input
                    v-model="interval"
                    type="radio"
                    class="mr-2"
                    value="28"
                  > 4 Week</label>
                  <label class="mb-1 block font-bold"><input
                    v-model="interval"
                    type="radio"
                    class="mr-2"
                    value="7"
                  > Weekly</label>
                  <label class="mb-1 block font-bold"><input
                    v-model="interval"
                    type="radio"
                    class="mr-2"
                    value="1"
                  > Daily</label>
                </div>
              </div>

              <div class="mt-4">
                <div class="mb-1 flex items-center gap-1">
                  <label for="headerRow" class="block font-bold">Header Row</label>
                  <Button
                    v-tooltip="headerRowTooltip"
                    icon="pi pi-question-circle"
                    variant="text"
                    rounded
                    severity="secondary"
                    size="small"
                    type="button"
                    aria-label="Header row help"
                    class="!h-6 !w-6 !p-0"
                  />
                </div>
                <input
                  id="headerRow"
                  v-model.number="headerRow"
                  type="number"
                  min="1"
                >
              </div>

              <div class="mt-4">
                <div class="mb-1 flex items-center gap-1">
                  <label for="firstDataRow" class="block font-bold">First Data Row</label>
                  <Button
                    v-tooltip="firstDataRowTooltip"
                    icon="pi pi-question-circle"
                    variant="text"
                    rounded
                    severity="secondary"
                    size="small"
                    type="button"
                    aria-label="First data row help"
                    class="!h-6 !w-6 !p-0"
                  />
                </div>
                <input
                  id="firstDataRow"
                  v-model.number="firstDataRow"
                  type="number"
                  min="2"
                >
              </div>

              <div class="mb-1 flex items-center gap-1">
                <label class="mt-4 flex items-center gap-2 font-bold">
                  <input
                    v-model="downloadPreUpdateImpressions"
                    type="checkbox"
                  >
                  Download Pre-Update Impressions
                </label>
                <Button
                  v-tooltip="downloadPreUpdateImpressionsTooltip"
                  icon="pi pi-question-circle"
                  variant="text"
                  rounded
                  severity="secondary"
                  size="small"
                  type="button"
                  aria-label="Download Pre-Update Impressions help"
                  class="!h-6 !w-6 !p-0"
                />
              </div>

              <div class="mt-4">
                <Button
                  label="Upload File"
                  type="submit"
                  :disabled="!file || isUploading"
                />
              </div>

              <p v-if="message" class="mt-4 whitespace-pre-line text-green-600">
                {{ message }}
              </p>
              <p v-if="error" class="mt-4 whitespace-pre-line text-red-600">
                {{ error }}
              </p>
              <p v-if="warning" class="mt-4 whitespace-pre-line text-yellow-600">
                {{ warning }}
              </p>
            </form>
          </div>
          <div>
            <a
              href="/docs/templates/upload-impressions-template.csv"
              class="text-[var(--text-link-primary)] hover:underline"
            >
              Download Template CSV
            </a>
            <br>
            <a
              href="/docs/templates/upload-impressions-template.xlsx"
              class="text-[var(--text-link-primary)] hover:underline"
            >
              Download Template XLSX
            </a>
          </div>
        </div>
      </Section>
    </UserRoleGate>
  </AppContainer>
</template>
